// Onboarding & Exits queue (Lifecycle L2). Every employee enters and leaves
// through a controlled checklist — this page lists the running (and past)
// instances and lets HR start a new onboarding/offboarding. The task board
// itself lives on the employee profile.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Loader2, Search, ShieldAlert, UserMinus, UserPlus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { useHasPermission } from '@/rbac/useRbac';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { getAllEmployees, type Employee } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getLifecyclePackages, listLifecycleInstances, startLifecycleInstance,
  exitTypeLabel, FALLBACK_EXIT_TYPES,
  type LifecycleInstanceRow, type LifecyclePackage, type LifecycleStatusFilter, type LifecycleType,
} from '@/services/lifecycleService';

const STATUS_FILTERS: LifecycleStatusFilter[] = ['in_progress', 'completed', 'cancelled', 'all'];

const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const TYPE_BADGE: Record<string, string> = {
  onboarding: 'bg-success-soft text-success-strong border-transparent',
  offboarding: 'bg-danger-soft text-danger-strong border-transparent',
};

const STATUS_BADGE: Record<string, string> = {
  in_progress: 'bg-warning-soft text-warning-strong border-transparent',
  completed: 'bg-success-soft text-success-strong border-transparent',
  cancelled: 'bg-muted text-muted-foreground border-border',
};

// ---------------------------------------------------------- start dialog
const StartInstanceDialog: React.FC<{
  type: LifecycleType;
  open: boolean;
  onClose: () => void;
  onStarted: (employeeId: string) => void;
}> = ({ type, open, onClose, onStarted }) => {
  const { t } = useTranslation('admin');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [packages, setPackages] = useState<LifecyclePackage[]>([]);
  const [exitTypes, setExitTypes] = useState<string[]>([...FALLBACK_EXIT_TYPES]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [packageName, setPackageName] = useState(''); // '' = automatic match
  // Exit details (offboarding only). No default exit type — force a choice.
  const [exitType, setExitType] = useState('');
  const [lastWorkingDate, setLastWorkingDate] = useState('');
  const [finalPayrollDate, setFinalPayrollDate] = useState('');
  const [reason, setReason] = useState('');
  const [starting, setStarting] = useState(false);

  const isOffboarding = type === 'offboarding';
  const flowLabel = isOffboarding
    ? t('lifecycle.offboarding', { defaultValue: 'Offboarding' })
    : t('lifecycle.onboarding', { defaultValue: 'Onboarding' });

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setSearch('');
    setShowAll(false);
    setEmployeeId('');
    setPackageName('');
    setExitType('');
    setLastWorkingDate('');
    setFinalPayrollDate('');
    setReason('');
    Promise.all([
      getAllEmployees().catch(() => [] as Employee[]),
      getLifecyclePackages().catch(() => null),
    ])
      .then(([emps, pkgRes]) => {
        if (!alive) return;
        setEmployees(emps);
        setPackages((pkgRes?.packages || []).filter((p: LifecyclePackage) => p.type === type));
        if (pkgRes?.exit_types?.length) setExitTypes(pkgRes.exit_types);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, type]);

  // Default focus: onboarding → Draft hires waiting to be made payroll-ready;
  // offboarding → Active staff. "Show all" lifts the filter.
  const defaultStatus = isOffboarding ? 'active' : 'draft';
  const term = search.trim().toLowerCase();
  const listed = useMemo(() => employees
    .filter((e) => showAll || (e.status || '').toLowerCase() === defaultStatus)
    .filter((e) => {
      if (!term) return true;
      const name = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
      return name.includes(term)
        || (e.email || '').toLowerCase().includes(term)
        || (e.employee_id || '').toLowerCase().includes(term);
    })
    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)),
  [employees, showAll, defaultStatus, term]);

  // Offboarding requires exit type + last working day (backend 409s without them).
  const missingExitFields = isOffboarding && (!exitType || !lastWorkingDate);

  const start = async () => {
    if (!employeeId || missingExitFields) return;
    setStarting(true);
    try {
      const res = await startLifecycleInstance({
        employee_id: employeeId,
        type,
        ...(packageName ? { package_name: packageName } : {}),
        ...(isOffboarding ? {
          exit_type: exitType,
          last_working_date: lastWorkingDate,
          ...(finalPayrollDate ? { final_payroll_date: finalPayrollDate } : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        } : {}),
      });
      toast.success(t('lifecycle.started_toast', {
        defaultValue: '{{flow}} started with the “{{pkg}}” package.',
        flow: flowLabel,
        pkg: res.package_name,
      }));
      onStarted(employeeId);
      onClose();
    } catch (e: any) {
      // 409 = an instance is already in progress for this employee.
      toast.error(getApiErrorMessage(e, errMsg(e, t('lifecycle.start_failed', { defaultValue: 'Could not start the checklist.' }))));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !starting && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('lifecycle.start_title', { defaultValue: 'Start {{flow}}', flow: flowLabel.toLowerCase() })}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {t('lifecycle.loading', { defaultValue: 'Loading…' })}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Employee picker (searchable) */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="lc-emp-search">
                {t('lifecycle.employee', { defaultValue: 'Employee' })}
              </label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="lc-emp-search"
                  className="w-full h-9 pl-8 pr-3 rounded-md border border-border bg-card text-sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('lifecycle.search_ph', { defaultValue: 'Search by name, email or ID…' })}
                />
              </div>
              <div className="max-h-52 overflow-y-auto rounded-md border border-border divide-y divide-border">
                {listed.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">
                    {showAll
                      ? t('lifecycle.no_employees', { defaultValue: 'No employees match.' })
                      : isOffboarding
                        ? t('lifecycle.no_active', { defaultValue: 'No active employees match.' })
                        : t('lifecycle.no_drafts', { defaultValue: 'No draft employees match. Use “Show all employees” to pick anyone.' })}
                  </p>
                ) : listed.map((e) => {
                  const name = `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email;
                  const selected = e.id === employeeId;
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => setEmployeeId(selected ? '' : e.id)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition-colors ${
                        selected ? 'bg-orange-50 dark:bg-orange-500/[0.12] text-orange-800 dark:text-orange-300' : 'hover:bg-muted/60 text-foreground'
                      }`}
                    >
                      <span className="min-w-0 truncate">
                        {name}
                        {e.employee_id ? <span className="text-muted-foreground"> · {e.employee_id}</span> : null}
                      </span>
                      <span className="text-xs text-muted-foreground capitalize shrink-0">{e.status || '—'}</span>
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  className="w-3.5 h-3.5 accent-orange-500"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                />
                {t('lifecycle.show_all', { defaultValue: 'Show all employees' })}
                {!showAll && (
                  <span>
                    ({isOffboarding
                      ? t('lifecycle.showing_active', { defaultValue: 'showing Active only' })
                      : t('lifecycle.showing_draft', { defaultValue: 'showing Draft only' })})
                  </span>
                )}
              </label>
            </div>

            {/* Exit details — offboarding only (backend requires type + last day) */}
            {isOffboarding && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground" htmlFor="lc-exit-type">
                      {t('lifecycle.exit_type', { defaultValue: 'Exit type' })}
                      <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <select
                      id="lc-exit-type"
                      className="w-full h-9 px-3 rounded-md border border-border bg-card text-sm"
                      value={exitType}
                      onChange={(e) => setExitType(e.target.value)}
                    >
                      <option value="" disabled>
                        {t('lifecycle.exit_type_ph', { defaultValue: 'Select an exit type…' })}
                      </option>
                      {exitTypes.map((et) => (
                        <option key={et} value={et}>{exitTypeLabel(t, et)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground" htmlFor="lc-last-day">
                      {t('lifecycle.last_working_day', { defaultValue: 'Last working day' })}
                      <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <input
                      id="lc-last-day"
                      type="date"
                      className="w-full h-9 px-3 rounded-md border border-border bg-card text-sm"
                      value={lastWorkingDate}
                      onChange={(e) => setLastWorkingDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="lc-final-payroll">
                    {t('lifecycle.final_payroll_date', { defaultValue: 'Final payroll date' })}
                  </label>
                  <input
                    id="lc-final-payroll"
                    type="date"
                    className="w-full h-9 px-3 rounded-md border border-border bg-card text-sm"
                    value={finalPayrollDate}
                    onChange={(e) => setFinalPayrollDate(e.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t('lifecycle.final_payroll_hint', { defaultValue: 'The last payroll run this person should appear in' })}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="lc-reason">
                    {t('lifecycle.exit_reason', { defaultValue: 'Reason (optional)' })}
                  </label>
                  <textarea
                    id="lc-reason"
                    rows={2}
                    maxLength={2000}
                    className="w-full px-3 py-2 rounded-md border border-border bg-card text-sm resize-y"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t('lifecycle.exit_reason_ph', { defaultValue: 'Context for the exit — kept on the record.' })}
                  />
                </div>
              </>
            )}

            {/* Package pick — Automatic matches by employment type (onboarding)
                or exit type (offboarding) */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="lc-package">
                {t('lifecycle.package', { defaultValue: 'Package' })}
              </label>
              <select
                id="lc-package"
                className="w-full h-9 px-3 rounded-md border border-border bg-card text-sm"
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
              >
                <option value="">
                  {isOffboarding
                    ? t('lifecycle.package_auto_exit', { defaultValue: 'Automatic (match by exit type)' })
                    : t('lifecycle.package_auto', { defaultValue: 'Automatic (match by employment type)' })}
                </option>
                {packages.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={starting}>
            {t('lifecycle.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={start} disabled={!employeeId || missingExitFields || starting || loading}>
            {starting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('lifecycle.start_action', { defaultValue: 'Start {{flow}}', flow: flowLabel.toLowerCase() })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------------------------------------------- page
export const LifecycleQueuePage: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const location = useLocation();
  const { isManager } = useRoleAccess();
  const canWrite = useHasPermission('employee:write');

  const [statusFilter, setStatusFilter] = useState<LifecycleStatusFilter>('in_progress');
  const [typeFilter, setTypeFilter] = useState<LifecycleType | 'all'>('all');
  const [rows, setRows] = useState<LifecycleInstanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [startType, setStartType] = useState<LifecycleType | null>(null);

  // Base path so employee links work under both /dashboard/:slug and /t/:slug/app.
  const appBase = useMemo(() => location.pathname.replace(/\/admin\/lifecycle.*$/, ''), [location.pathname]);
  const overdueCount = useMemo(() => rows.filter((r) => r.overdue).length, [rows]);
  const goToEmployee = (id: string) => navigate(`${appBase}/employees/${id}`);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listLifecycleInstances(statusFilter, typeFilter === 'all' ? undefined : typeFilter));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('lifecycle.load_failed', { defaultValue: 'Could not load the lifecycle queue.' }))));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, t]);

  useEffect(() => { if (isManager) void load(); }, [isManager, load]);

  const statusLabel: Record<LifecycleStatusFilter, string> = {
    in_progress: t('lifecycle.filter_in_progress', { defaultValue: 'In progress' }),
    completed: t('lifecycle.filter_completed', { defaultValue: 'Completed' }),
    cancelled: t('lifecycle.filter_cancelled', { defaultValue: 'Cancelled' }),
    all: t('lifecycle.filter_all', { defaultValue: 'All' }),
  };
  const typeLabel: Record<'all' | LifecycleType, string> = {
    all: t('lifecycle.type_all', { defaultValue: 'All types' }),
    onboarding: t('lifecycle.onboarding', { defaultValue: 'Onboarding' }),
    offboarding: t('lifecycle.offboarding', { defaultValue: 'Offboarding' }),
  };

  if (!isManager) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">{t('lifecycle.admins_only', { defaultValue: 'Managers & admins only' })}</h1>
        <p className="text-muted-foreground mt-1">
          {t('lifecycle.no_permission', { defaultValue: 'You do not have permission to view onboarding & exits.' })}
        </p>
      </div>
    );
  }

  const chipCls = (active: boolean) =>
    `h-8 px-3 rounded-full border text-sm transition-colors ${
      active
        ? 'bg-orange-500 border-orange-500 text-white'
        : 'bg-card border-border text-foreground hover:border-orange-300 dark:hover:border-orange-500/40'
    }`;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 text-charcoal flex items-center gap-2">
            <UserPlus className="w-6 h-6" /> {t('lifecycle.title', { defaultValue: 'Onboarding & Exits' })}
            {overdueCount > 0 && (
              <span className="text-xs font-medium bg-danger-soft text-danger-strong border border-transparent rounded-full px-2.5 py-0.5">
                {t('lifecycle.overdue_count', { defaultValue: '{{count}} overdue exits', count: overdueCount })}
              </span>
            )}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('lifecycle.subtitle', {
              defaultValue: 'Every employee enters and leaves through a controlled checklist. Onboarding makes someone payroll-ready; offboarding closes them out cleanly.',
            })}
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button onClick={() => setStartType('onboarding')}>
              <UserPlus className="w-4 h-4 mr-1" /> {t('lifecycle.start_onboarding', { defaultValue: 'Start onboarding' })}
            </Button>
            <Button variant="outline" className="text-danger border-red-200 hover:bg-red-50 dark:border-red-500/25" onClick={() => setStartType('offboarding')}>
              <UserMinus className="w-4 h-4 mr-1" /> {t('lifecycle.start_offboarding', { defaultValue: 'Start offboarding' })}
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setStatusFilter(f)} className={chipCls(statusFilter === f)}>
            {statusLabel[f]}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
        {(['all', 'onboarding', 'offboarding'] as const).map((f) => (
          <button key={f} type="button" onClick={() => setTypeFilter(f)} className={chipCls(typeFilter === f)}>
            {typeLabel[f]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('lifecycle.loading_queue', { defaultValue: 'Loading checklists…' })}
        </div>
      ) : rows.length === 0 ? (
        <Card className="border-orange-100 dark:border-orange-500/20">
          <CardContent className="py-12 text-center text-muted-foreground">
            {statusFilter === 'in_progress'
              ? t('lifecycle.empty_in_progress', { defaultValue: 'No checklists in progress. Start one for a new hire or a leaver.' })
              : t('lifecycle.empty', { defaultValue: 'No checklists match this filter.' })}
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-4 py-3 font-medium">{t('lifecycle.col_employee', { defaultValue: 'Employee' })}</th>
                <th className="px-4 py-3 font-medium">{t('lifecycle.col_type', { defaultValue: 'Type' })}</th>
                <th className="px-4 py-3 font-medium">{t('lifecycle.col_package', { defaultValue: 'Package' })}</th>
                <th className="px-4 py-3 font-medium">{t('lifecycle.col_status', { defaultValue: 'Checklist' })}</th>
                <th className="px-4 py-3 font-medium">{t('lifecycle.col_emp_status', { defaultValue: 'Employee status' })}</th>
                <th className="px-4 py-3 font-medium">{t('lifecycle.col_started', { defaultValue: 'Started' })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => goToEmployee(r.employee.id)}
                  className="cursor-pointer hover:bg-orange-50/60 dark:hover:bg-orange-500/[0.06] transition-colors"
                >
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); goToEmployee(r.employee.id); }}
                      className="text-foreground font-medium hover:text-orange-700 hover:underline"
                    >
                      {r.employee.name}
                    </button>
                    {r.employee.employee_code && (
                      <span className="text-xs text-muted-foreground ml-1.5">{r.employee.employee_code}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge className={`border ${TYPE_BADGE[r.type] || TYPE_BADGE.onboarding}`}>{typeLabel[r.type] || r.type}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-foreground">
                    {r.package_name}
                    {r.type === 'offboarding' && r.exit_type && (
                      <span className="ml-1.5 text-[11px] text-muted-foreground bg-muted border border-border rounded-full px-2 py-0.5 whitespace-nowrap">
                        {exitTypeLabel(t, r.exit_type)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <Badge className={`border ${STATUS_BADGE[r.status] || STATUS_BADGE.cancelled}`}>
                        {statusLabel[r.status as LifecycleStatusFilter] || r.status}
                      </Badge>
                      {r.overdue && (
                        <Badge className="border bg-danger-soft text-danger-strong border-transparent">
                          {t('lifecycle.overdue', { defaultValue: 'Overdue' })}
                        </Badge>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground capitalize">{r.employee.status || '—'}</td>
                  <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{fmtDate(r.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {startType && (
        <StartInstanceDialog
          type={startType}
          open={!!startType}
          onClose={() => setStartType(null)}
          onStarted={() => void load()}
        />
      )}
    </div>
  );
};

export default LifecycleQueuePage;
